/**
 * Individual message component
 * Renders different message types with appropriate styling using MUI components
 */

import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import type { Message as MessageType } from '../../types';
import { useTheme } from '@mui/material/styles';

interface MessageProps {
  message: MessageType;
  variant?: 'default' | 'conversationOverlay';
}

export const Message: React.FC<MessageProps> = ({ message, variant = 'default' }) => {

  const theme = useTheme();
  // Define animations
  const fadeIn = keyframes`
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  const blink = keyframes`
    0%, 60%, 100% {
      opacity: 0.3;
    }
    30% {
      opacity: 1;
    }
  `;

  /**
   * Get message styling based on type
   */
  const getMessageSx = () => {
    if (variant === 'conversationOverlay') {
      const overlayBaseSx = {
        margin: '0 0 10px',
        padding: '14px 16px',
        borderRadius: '8px',
        animation: `${fadeIn} 0.3s ease-in`,
        color: '#111827',
        width: 'fit-content',
        maxWidth: 286,
        boxShadow: 'none',
        fontSize: '0.98rem',
        lineHeight: 1.5,
        letterSpacing: '0.005em',
        fontWeight: 400,
      };

      switch (message.type) {
        case 'medical-provider':
          return {
            ...overlayBaseSx,
            background: '#f4f4f4',
            color: '#111827',
            marginLeft: 'auto',
            marginRight: 0,
            minHeight: 50,
          };
        case 'patient':
          return {
            ...overlayBaseSx,
            background: '#3399A3',
            color: '#ffffff',
            marginLeft: 0,
            marginRight: 'auto',
            minHeight: 50,
          };
        case 'ai-thinking':
          return {
            ...overlayBaseSx,
            background: '#3399A3',
            color: '#ffffff',
            marginLeft: 0,
            marginRight: 'auto',
            fontStyle: 'italic',
            minHeight: 50,
          };
        case 'system':
          return {
            ...overlayBaseSx,
            width: '100%',
            maxWidth: '100%',
            background: '#ffffff',
            border: '1px solid rgba(15, 23, 42, 0.10)',
            color: '#111827',
            textAlign: 'center',
            fontStyle: 'italic',
            marginLeft: 0,
            marginRight: 0,
            boxSizing: 'border-box',
          };
        default:
          return {
            ...overlayBaseSx,
            background: '#f4f4f4',
            color: '#111827',
          };
      }
    }

    const baseSx = {
      margin: '12px 0',
      padding: '14px 16px',
      borderRadius: '18px',
      animation: `${fadeIn} 0.3s ease-in`,
      color: 'text.primary',
      width: 'fit-content',
      maxWidth: '88%',
      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.06)',
    };

    switch (message.type) {
      case 'medical-provider':
        return {
          ...baseSx,
          background: '#3399A3',
          color: '#ffffff',
          textAlign: 'left',
          marginLeft: 'auto',
          borderBottomRightRadius: '8px',
        };
      case 'patient':
        return {
          ...baseSx,
          background: '#f7f7f5',
          color: '#1f2937',
          textAlign: 'left',
          marginRight: 'auto',
          border: `1px solid ${theme.palette.divider}`,
          borderBottomLeftRadius: '8px',
        };
      case 'system':
        return {
          ...baseSx,
          width: '100%',
          maxWidth: '100%',
          backgroundColor: 'rgba(255,255,255,0.8)',
          border: `1px solid ${theme.palette.divider}`,
          textAlign: 'center',
          fontStyle: 'italic',
          boxShadow: 'none',
        };
      case 'ai-thinking':
        return {
          ...baseSx,
          backgroundColor: 'rgba(255,255,255,0.8)',
          border: `1px solid ${theme.palette.divider}`,
          textAlign: 'left',
          fontStyle: 'italic',
          opacity: 0.8,
          marginRight: 'auto',
        };
      default:
        return {
          ...baseSx,
          color: '#dc3545',
          fontStyle: 'italic',
        };
    }
  };

  /**
   * Get prefix based on message type
   */
  const getPrefix = () => {
    if (variant === 'conversationOverlay') {
      return '';
    }

    switch (message.type) {
      case 'medical-provider':
        return 'Medical Provider: ';
      case 'patient':
        return 'Patient: ';
      case 'system':
        return 'System: ';
      case 'ai-thinking':
        return '';
      default:
        return '';
    }
  };

  /**
   * Format content with line breaks for system messages
   */
  const formatContent = (content: string) => {
    if (message.type === 'system') {
      // Split by newlines and render each line
      return content.split('\n').map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < content.split('\n').length - 1 && <br />}
        </React.Fragment>
      ));
    }
    return content;
  };

  /**
   * Render thinking animation
   */
  const renderThinkingAnimation = () => {
    if (message.type === 'ai-thinking') {
      return (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            marginLeft: '5px',
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              animation: `${blink} 1.4s infinite`,
            }}
          >
            .
          </Box>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              animation: `${blink} 1.4s infinite`,
              animationDelay: '0.2s',
            }}
          >
            .
          </Box>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              animation: `${blink} 1.4s infinite`,
              animationDelay: '0.4s',
            }}
          >
            .
          </Box>
        </Box>
      );
    }
    return null;
  };

  /**
   * Format timestamp for display (if needed in the future)
   */
  const formatTimestamp = (timestampStr: string) => {
    try {
      const date = new Date(timestampStr);
      return date.toLocaleTimeString();
    } catch {
      return '';
    }
  };

  return (
    <Box
      sx={getMessageSx()}
      title={formatTimestamp(message.timestamp)} // Show timestamp on hover
    >
      <Typography
        component="span"
        sx={{
          fontWeight: variant === 'conversationOverlay' ? '400 !important' : 'bold',
        }}
      >
        {getPrefix()}
      </Typography>
      <Typography
        component="span"
        sx={{
          display: 'inline-block',
          wordWrap: 'break-word',
          maxWidth: '100%',
          fontSize: variant === 'conversationOverlay' ? '0.98rem' : undefined,
          fontWeight: variant === 'conversationOverlay' ? '400 !important' : undefined,
          lineHeight: variant === 'conversationOverlay' ? 1.5 : undefined,
        }}
      >
        {formatContent(message.content)}
        {renderThinkingAnimation()}
      </Typography>
    </Box>
  );
};
